/**
 * useAsaasAccount — I/O da tela de onboarding da subconta Asaas (caminho de
 * TESTE/sandbox — ver o comentário no topo de `AsaasOnboardingPage.tsx`).
 *
 * Três consultas/mutações, deliberadamente separadas de `usePaymentDetection`:
 * aquele hook é sobre a CONFIGURAÇÃO de detecção automática (o interruptor);
 * este é sobre a SUBCONTA em si — criá-la e acompanhar o checklist de
 * documentos do onboarding por link. Compartilhar um hook para os dois
 * misturaria "ligar a detecção" com "abrir uma conta de pagamento", que são
 * decisões diferentes tomadas em momentos diferentes.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useRole } from '@/hooks/useRole';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import {
  asaasDocumentsQueryReady,
  buildAsaasOnboardPayload,
  type AsaasDocumentGroup,
  type AsaasOnboardFormValues,
} from '@/lib/asaasOnboarding';

const ACCOUNT_KEY = 'asaas-onboarding-account';
const DOCUMENTS_KEY = 'asaas-onboarding-documents';

interface AsaasAccountRow {
  status: string;
  status_detail: string | null;
  pix_key_ready: boolean;
  environment: string;
  created_at: string;
}

interface DocumentsResponse {
  accountStatus: string;
  statusDetail: string | null;
  accountCreatedAt: string;
  rejectReasons: string | null;
  groups: AsaasDocumentGroup[];
}

class OnboardError extends Error {}

export function useAsaasAccount() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isAdmin } = useRole();
  const queryClient = useQueryClient();

  const companyId = activeCompany?.id ?? null;
  // Mesmo gate do servidor (`requireCompanyAdmin`, owner OU admin): mostrar
  // este fluxo para quem a função vai recusar seria convidar para um 403.
  const ready = Boolean(user?.id && companyId && isAdmin);

  const accountQuery = useQuery({
    queryKey: [ACCOUNT_KEY, companyId],
    enabled: ready,
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // A query nasce desabilitada até a empresa resolver — sem o carimbo, o
    // primeiro load some da fila para sempre (#158).
    initialDataUpdatedAt: 0,
    queryFn: async (): Promise<AsaasAccountRow | null> => {
      if (!companyId) return null;
      const { data, error } = await supabase
        .from('asaas_accounts' as never)
        .select('status, status_detail, pix_key_ready, environment, created_at')
        .eq('company_id', companyId);
      if (error) throw error;
      const rows = (data as unknown as AsaasAccountRow[] | null) ?? [];
      if (rows.length === 0) return null;
      // A tabela é única por (empresa, AMBIENTE); o app não sabe qual
      // ambiente o deploy usa, então SANDBOX é preferido aqui — é o único
      // ambiente que este fluxo de teste deve exercitar (produção fica
      // congelada, addendum do #165).
      return rows.find((r) => r.environment === 'sandbox') ?? rows[0];
    },
  });

  const account = accountQuery.data ?? null;

  const documentsReady = Boolean(
    account && asaasDocumentsQueryReady(account.created_at, Date.now()),
  );

  const documentsQuery = useQuery({
    queryKey: [DOCUMENTS_KEY, companyId],
    enabled: ready && documentsReady,
    networkMode: 'offlineFirst',
    staleTime: 15 * 1000,
    refetchOnMount: 'always',
    initialDataUpdatedAt: 0,
    queryFn: async (): Promise<DocumentsResponse> => {
      const { data, error } = await supabase.functions.invoke('asaas-account-documents', {
        body: { companyId },
      });
      if (error) {
        const body = (data ?? null) as { code?: string; message?: string } | null;
        throw new OnboardError(body?.message ?? 'Não foi possível consultar os documentos.');
      }
      return data as DocumentsResponse;
    },
  });

  const createAccount = useMutation({
    networkMode: 'offlineFirst' as const,
    mutationFn: async (values: AsaasOnboardFormValues) => {
      if (!companyId) throw new OnboardError('Nenhuma empresa ativa.');
      if (!(await checkNetworkStatus())) throw new OnboardError('offline');

      const { data, error } = await supabase.functions.invoke('asaas-onboard', {
        body: buildAsaasOnboardPayload(companyId, values),
      });
      if (error) {
        const body = (data ?? null) as { code?: string; message?: string } | null;
        throw new OnboardError(onboardErrorMessage(body?.code, body?.message));
      }
      return data;
    },
    onSuccess: () => {
      toast.success('Subconta criada. Agora envie os documentos solicitados.');
      void queryClient.invalidateQueries({ queryKey: [ACCOUNT_KEY, companyId] });
    },
    onError: (error: Error) => {
      if (error.message === 'offline') {
        toast.warning('Criar a subconta exige conexão. Conecte-se e tente de novo.');
        return;
      }
      toast.error(error.message || 'Não foi possível criar a subconta.');
    },
  });

  return {
    ready,
    account,
    accountLoading: accountQuery.isLoading,
    documents: documentsQuery.data?.groups ?? [],
    documentsRejectReasons: documentsQuery.data?.rejectReasons ?? null,
    documentsLoading: documentsQuery.isLoading,
    // Enquanto o Asaas ainda valida o cadastro (janela de 15s da própria
    // documentação deles), a tela diz isso em vez de tentar e falhar.
    documentsPending: Boolean(account) && !documentsReady,
    creating: createAccount.isPending,
    createAccount: (values: AsaasOnboardFormValues) => createAccount.mutateAsync(values),
  };
}

function onboardErrorMessage(code: string | undefined, message: string | undefined): string {
  switch (code) {
    case 'plan_required':
      return 'Seu plano não libera este recurso.';
    case 'already_exists':
      return 'Esta empresa já tem uma subconta.';
    case 'invalid_cpf_cnpj':
      return 'CPF/CNPJ inválido.';
    case 'forbidden':
      return 'Você não tem permissão para conectar a conta de pagamento desta empresa.';
    case 'webhook_token_missing':
    case 'asaas_not_configured':
      return 'O ambiente de pagamento não está configurado neste servidor.';
    default:
      return message || 'Não foi possível criar a subconta.';
  }
}
